import React from 'react'
import { LayoutBand, H4, Row, Image, usePageWidth, Separator, Button, DialogOverlay, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { Link } from '@fs/zion-router'
import ZionForm, { FormTextField, useZionForm } from '@fs/zion-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { baseUrl } from '@fs/zion-config'
import { useFeatureFlag } from '@fs/zion-flags'
import immigrationRecordsImg from './images/immigration-records-image.webp'
import africanHeritageImg from './images/african-heritage-image.png'
import census1950Img from './images/1950-census-image.png'
import canadaFlagImg from './images/canada-flag-image.svg'

export default function SpecialCollections() {
  const { t } = useTranslation()
  const overlay = useOverlay()
  const atPageWidth = usePageWidth()
  const is1950CensusRedirect = useFeatureFlag('platform_SEO_1950CensusRedirect')?.isOn
  return (
    <LayoutBand marginX="xl">
      <Separator size={atPageWidth({ default: 'lg', lg: 'md' })} />
      <H4 size="H5">{t('', 'Discover these popular resources:')}</H4>
      <Separator size={atPageWidth({ default: 'md', lg: 'md' })} />
      <Row alignX="center" wrap>
        <CollectionLink
          src={immigrationRecordsImg}
          alt={t(
            'nlihp.united-states.special-collections.immigration-records.image-alt',
            'Little girl disembarking a ship on Ellis Island'
          )}
          to="/united-states/immigration-records"
          linkText={t('nlihp.united-states.special-collections.immigration-records', 'Immigration records')}
          growX
        />
        <CollectionLink
          src={africanHeritageImg}
          alt={t(
            'nlihp.united-states.special-collections.african-american-records.image-alt',
            'Young African descent boy looking at a computer'
          )}
          to="/united-states/ethnicities/african-american"
          linkText={t('nlihp.united-states.special-collections.african-american-records', 'African American records')}
          growX
        />
        <CollectionLink
          src={census1950Img}
          alt={t('nlihp.united-states.special-collections.1950-census.image-alt', '1950 U.S. Census Special Logo')}
          to={is1950CensusRedirect ? '/search/collection/4464515' : '/1950Census'}
          linkText={t('nlihp.united-states.special-collections.1950-census', '1950 US Census records')}
          growX
        />
        <div growX>
          <CollectionImage
            src={canadaFlagImg}
            alt={t('nlihp.united-states.special-collections.canadian-census.image-alt', 'Canadian flag')}
          />
          <Separator size="sm" />
          <Row alignX="center">
            <Link
              emphasis="low"
              onClick={(event) => {
                event.preventDefault()
                overlay.handleClick(event)
              }}
              color="gray"
              to="/search/discovery/results/?tab=records&results=12&f.collectionId=5000196&f.collectionId=2143998&f.collectionId=1584557&f.collectionId=1583536&f.collectionId=1804541&f.collectionId=1551612&f.collectionId=1325192"
              linkName="Canada census records"
              isGray
              wayfinding
            >
              {t('nlihp.united-states.special-collections.canadian-census', 'Canada census records')}
            </Link>
          </Row>
        </div>
      </Row>
      <Separator size={atPageWidth({ default: 'xxl', lg: 'xl' })} />
      <CanadaCensusSearchOverlay overlay={overlay} />
    </LayoutBand>
  )
}

function CollectionLink({ src, alt, to, linkText }) {
  return (
    <>
      <CollectionImage src={src} alt={alt} />
      <Separator size="sm" />
      <Row alignX="center">
        <Link external to={to} isGray wayfinding linkName={linkText} target="_blank">
          {linkText}
        </Link>
      </Row>
    </>
  )
}

function CollectionImage({ src, alt }) {
  const atPageWidth = usePageWidth()
  return <Image height={atPageWidth({ default: '150px', lg: '200px' })} src={src} alt={alt} noCrop />
}

function CanadaCensusSearchOverlay({ overlay }) {
  const { t } = useTranslation()
  const validationSchema = Yup.object().shape({
    firstNames: Yup.string(),
    lastNames: Yup.string().required('Required'),
    placeLived: Yup.string(),
    birthYear: Yup.string(),
  })
  const methods = useZionForm({
    defaultValues: { firstNames: '', lastNames: '', placeLived: '', birthYear: '' },
    resolver: yupResolver(validationSchema),
  })
  const {
    formState: { isValid, isDirty },
  } = methods
  const handleSubmit = (formValues) => {
    window.location.href = getCanadaSearchUrl(formValues)
  }
  return (
    <DialogOverlay title={t('', 'Search Canada Census records')} {...overlay}>
      <ZionForm methods={methods} onSubmit={handleSubmit}>
        <FormTextField
          label={t('nlihp.united-states.special-collections.canadian-census.first-names', 'First Names')}
          placeholder={t('nlihp.united-states.special-collections.canadian-census.first-names', 'First Names')}
          id="firstNames"
          name="firstNames"
        />
        <FormTextField
          label={t('nlihp.united-states.special-collections.canadian-census.last-names', 'Last Names')}
          placeholder={t('nlihp.united-states.special-collections.canadian-census.last-names', 'Last Names')}
          id="lastNames"
          name="lastNames"
        />
        {/** Required */}
        <FormTextField
          label={t('nlihp.united-states.special-collections.canadian-census.place-lived', 'Place Lived')}
          placeholder={t('nlihp.united-states.special-collections.canadian-census.place-lived', 'Place Lived')}
          id="placeLived"
          name="placeLived"
        />
        <FormTextField
          label={t('nlihp.united-states.special-collections.canadian-census.birth-year', 'Birth Year')}
          placeholder={t('nlihp.united-states.special-collections.canadian-census.birth-year', 'Birth Year')}
          id="birthYear"
          name="birthYear"
        />
        <Button emphasis="high" type="submit" disabled={!isValid || !isDirty} linkName="Search Canada Census records">
          {t('common-ui:search.action', 'Search')}
        </Button>
      </ZionForm>
    </DialogOverlay>
  )
}

export function getCanadaSearchUrl(formValues) {
  const { firstNames, lastNames, placeLived, birthYear } = formValues
  const searchParams = new URLSearchParams(
    '?tab=records&results=12&f.collectionId=5000196&f.collectionId=2143998&f.collectionId=1584557&f.collectionId=1583536&f.collectionId=1804541&f.collectionId=1551612&f.collectionId=1325192'
  )
  firstNames && searchParams.set('q.givenName', firstNames)
  lastNames && searchParams.set('q.surname', lastNames)
  placeLived && searchParams.set('q.anyPlace', placeLived)
  birthYear && searchParams.set('q.birthLikeDate.from', birthYear)
  return `${baseUrl}/search/discovery/results/?${searchParams.toString()}`
}
